'use client';
import React from 'react';
import Link from 'next/link';
import { Button, ButtonProps } from 'antd';
import styles from './button.module.scss';

type ButtonPropsWithoutIconPosition = Omit<ButtonProps, 'iconPosition' | 'variant'>;

interface ThemedButtonProps extends ButtonPropsWithoutIconPosition {
  theme?: 'primary' | 'secondary' | 'accent';
  variant?: 'solid' | 'outlined' | 'outline'; 
  text: string;
  icon?: React.ReactNode;
  iconPosition?: 'prefix' | 'suffix';
  loading?: boolean;
  href?: string;
  external?: boolean;
}


const isExternalHref = (href: string) =>
  /^([a-z]+:)?\/\//i.test(href) ||
  href.startsWith('mailto:') ||
  href.startsWith('tel:');

const ThemedButton: React.FC<ThemedButtonProps> = ({
  theme = 'primary',
  variant = 'solid',
  text,
  icon,
  iconPosition = 'prefix',
  loading = false,
  className,
  href,
  external,
  disabled,
  ...rest
}) => {
  const hasIcon = !!icon && !loading;

  const classes = [styles.btn, hasIcon ? styles.hasIcon : '', className || '']
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      {hasIcon && iconPosition === 'prefix' && (
        <span className={styles.icon} aria-hidden="true">
          {icon}
        </span>
      )}
      <span className={styles.label}>{text}</span>
      {hasIcon && iconPosition === 'suffix' && (
        <span className={styles.icon} aria-hidden="true">
          {icon}
        </span>
      )}
    </>
  );

  const normalizedVariant = variant === 'outlined' ? 'outline' : variant;

  const btn = (
    <Button
      type="default"
      className={classes}
      data-theme={theme}
      data-variant={normalizedVariant} // drives CSS behaviors
      loading={loading}
      disabled={loading || !!disabled}
      aria-busy={loading || undefined}
      aria-disabled={loading || !!disabled || undefined}
      {...rest}
    >
      {content}
    </Button>
  );

  if (!href) return btn;

  const isExt = typeof external === 'boolean' ? external : isExternalHref(href);
  return isExt ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.linkWrapper}
    >
      {btn}
    </a>
  ) : (
    <Link href={href} className={styles.linkWrapper}>
      {btn}
    </Link>
  );
};

export default ThemedButton;
